import { nanoid } from "nanoid";
import { FilterQuery } from "../../../context/FiltersProvider/types";
import { FilterCombinationPayload, FilterPayload } from "../../../datasources/api-client/data-contracts";
import { createRow } from "../nodeFactories";
import { FilterItem, GroupFilter } from "../types";

// Convert a single API rule into a UI row
//
// This is the smallest building block of the advanced form.
//
// Example API:
// {
//   id: "status",
//   operator: "eq",
//   value: ["active"]
// }
//
// becomes a UI row:
// {
//   type: "row",
//   value: { filter, operator, value }
// }
const mapRuleToRow = (rule: FilterPayload): FilterItem => {
	return {
		type: "row",
		value: {
			id: nanoid(),

			// filter definition id (link to registry)
			filter: rule.id,

			operator: rule.operator,

			value: rule.value,
		},
	};
};

// Convert a group from API into a UI group node
//
// A group represents a logical container (AND / OR)
// that can contain:
// - rows (simple filters)
// - nested groups (recursive structure)
//
// If the group has no filters, we ensure at least
// one empty row so the UI never breaks.
const mapGroupToItem = (group: FilterCombinationPayload): FilterItem => {
	const mappedGroup: GroupFilter = {
		id: nanoid(),

		// logical operator for the group
		rowOperator: group.operator ?? "and",

		// recursively map children
		rows: group.filters && group.filters.length > 0 ? group.filters.map(mapNodeToItem) : [createRow()],
	};

	return {
		type: "group",
		value: mappedGroup,
	};
};

// Generic dispatcher that decides if the node is:
// - a rule (leaf)
// - a group (branch)
//
// This is the recursive engine that rebuilds the tree
// from backend payload into UI state.
const mapNodeToItem = (node: FilterCombinationPayload | FilterPayload): FilterItem => {
	// Leaf node (rule)
	if ("id" in node) {
		return mapRuleToRow(node);
	}

	// Branch node (group)
	return mapGroupToItem(node);
};

// Entry point used by the advanced editor
//
// Converts an API query into a fully usable form state
// ready for the UI editor.
//
// Important behaviors:
// - If query is missing → create a default empty group
// - Always guarantees at least 1 row in the UI
// - Builds a recursive tree structure
export const advancedQueryToFormState = (query?: FilterQuery): FilterItem[] => {
	// Safe default state: empty group with one row
	if (!query) {
		return [
			{
				type: "group",
				value: {
					id: nanoid(),
					rowOperator: "and",
					rows: [createRow()],
				},
			},
		];
	}

	// Convert full query tree
	return [mapGroupToItem(query)];
};
